/* MIRRORED FILE — an identical copy lives in the other project:
 *   game-client:      src/utils/use-now-seconds.ts
 *   portal-frontend:  src/utils/use-now-seconds.ts
 * Keep byte-identical below this banner; apply any change to both copies in the
 * same commit. */

/**
 * The wall clock, in whole Unix seconds, for anything that counts down or up:
 * the game's quarter ring, the session's time left, the next meeting; the
 * portal's time a match has run.
 *
 * Every caller shares one interval, so a screen with five countdowns does not
 * run five timers; it starts with the first caller and stops with the last.
 * The server's times (`next_report_at`, a session's scheduled end) are Unix
 * seconds too, so a countdown is their difference with this.
 */

import { useSyncExternalStore } from 'react';

let nowSeconds = Math.floor(Date.now() / 1000);
const listeners = new Set<() => void>();
let timer: number | undefined;

/**
 * The second, as of the last tick — or, while no clock runs, as of now: the
 * first caller after an idle spell renders before it subscribes, and must not
 * paint a stale second.
 */
function snapshot(): number {
  if (timer === undefined) nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === undefined) {
    nowSeconds = Math.floor(Date.now() / 1000);
    timer = window.setInterval(() => {
      nowSeconds = Math.floor(Date.now() / 1000);
      listeners.forEach((notify) => notify());
    }, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.clearInterval(timer);
      timer = undefined;
    }
  };
}

/** A clock that does not tick: the caller is not subscribed. */
const stopped = () => () => {};

/**
 * Now, in whole Unix seconds; the caller re-renders when the second changes —
 * while `running`, so a clock only some of a screen's states need (a way in's
 * wait for its session) is not kept in the others.
 */
export function useNowSeconds(running = true): number {
  return useSyncExternalStore(running ? subscribe : stopped, snapshot, snapshot);
}

/**
 * Now, in whole Unix seconds, read once: for a moment taken — when a board
 * opened, when a server's state arrived — rather than a countdown, which
 * re-renders with useNowSeconds.
 */
export function currentSeconds(): number {
  return Math.floor(Date.now() / 1000);
}
